import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { Button, Notification } from '@telus-uds/components-web';
import Modal from '../../design/shared/modal';
import DataTable from './DataTable';
import LoadingSpinner from './LoadingSpinner';
import { getApiErrorMessage } from '../utils/apiErrorMessage';

// Phase 7: clicking a chart segment opens this modal with the underlying record rows,
// re-querying the matching "records" endpoint with the same date range/filters as the
// chart plus the clicked dimension value, paginated (20/page, capped at 50 server-side).
const DrillDownModal = ({
  open, onClose, title, columns, fetchRecords, queryParams
}) => {
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [result, setResult] = useState(null);

  useEffect(() => {
    if (open) setPage(1);
  }, [open, queryParams]);

  useEffect(() => {
    if (!open) return undefined;
    let cancelled = false;
    setLoading(true);
    setError(null);
    fetchRecords({ ...queryParams, page, pageSize: 20 })
      .then(res => {
        if (!cancelled) setResult(res.data);
      })
      .catch(err => {
        if (!cancelled) setError(getApiErrorMessage(err, 'Failed to load records'));
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, page, queryParams]);

  return (
    <Modal open={open} setOpen={onClose} header={title} size="large">
      {loading && <LoadingSpinner text="Loading records..." />}
      {error && (
        <Notification variant={{ validation: 'error' }}>
          <div>
            <strong>Error:</strong> {error}
          </div>
        </Notification>
      )}
      {!loading && !error && result && (
        <>
          <DataTable columns={columns} data={result.rows} />
          <div style={{
            display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: '1rem'
          }}
          >
            <Button variant={{ priority: 'low' }} disabled={page <= 1} onClick={() => setPage(p => p - 1)}>
              Previous
            </Button>
            <span style={{ fontSize: '0.875rem', color: '#666666' }}>
              Page {result.page} of {result.total_pages} ({result.total} records)
            </span>
            <Button
              variant={{ priority: 'low' }}
              disabled={page >= result.total_pages}
              onClick={() => setPage(p => p + 1)}
            >
              Next
            </Button>
          </div>
        </>
      )}
    </Modal>
  );
};

DrillDownModal.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  title: PropTypes.string.isRequired,
  columns: PropTypes.arrayOf(PropTypes.object).isRequired,
  fetchRecords: PropTypes.func.isRequired,
  queryParams: PropTypes.object // eslint-disable-line react/forbid-prop-types
};

DrillDownModal.defaultProps = {
  queryParams: {}
};

export default DrillDownModal;
